import { ethers } from "ethers";
import nacl from "tweetnacl";
import { BN254_SCALAR_FIELD } from "./notes";

/** Public record metadata. All fields are authenticated, never inferred from ciphertext. */
export interface OnchainRecoveryContext {
  chain: "evm" | "solana";
  /** Circuit chain domain as a bytes32 word (the chain id for EVM). */
  chainDomain: string;
  /** Pool/program bytes32; EVM addresses may also be supplied as 20 bytes. */
  pool: string;
  kind: 1 | 2 | 3;
  /** Output commitment, swap locator, or bundle locator, according to kind. */
  locator: string;
}

export interface CompleteNoteRecoveryPayload {
  kind: 1;
  random: bigint;
  tokenAddress: string;
  tokenType: 0 | 3 | 4;
  value: bigint;
}

export interface SwapSeedRecoveryPayload {
  kind: 2;
  random: bigint;
  tokenAddress: string;
  tokenType: 0 | 3 | 4;
}

export type OnchainNoteRecoveryPayload =
  | CompleteNoteRecoveryPayload
  | SwapSeedRecoveryPayload;

export const ONCHAIN_RECOVERY_VERSION = 1;
export const MAX_ONCHAIN_RECOVERY_BYTES = 2048;
const NOTE_HEADER_BYTES = 2 + 32 + 12;
const TAG_BYTES = 16;
const TOKEN_TYPES = new Set([0, 3, 4]);
const CONTEXT_DOMAIN = ethers.toUtf8Bytes(
  "StealthOS onchain recovery context v1",
);
const NOTE_KEY_DOMAIN = "StealthOS onchain note recovery X25519 v1";

export class OnchainRecoveryError extends Error {}

function invalid(): never {
  // Do not include caller-supplied values: payloads can contain note secrets.
  throw new OnchainRecoveryError("Invalid on-chain recovery data.");
}

export function recoveryWord(value: string, allowAddress = false): Uint8Array {
  if (
    typeof value !== "string" ||
    (!ethers.isHexString(value, 32) &&
      !(allowAddress && ethers.isHexString(value, 20)))
  )
    return invalid();
  return ethers.getBytes(ethers.zeroPadValue(value, 32));
}

export function recoveryBuffer(value: Uint8Array): ArrayBuffer {
  return new Uint8Array(value).buffer;
}

/** Canonical AAD shared with the root-only master bundle codec. */
export function encodeOnchainRecoveryContext(
  context: OnchainRecoveryContext,
): Uint8Array {
  if (
    !context ||
    !["evm", "solana"].includes(context.chain) ||
    ![1, 2, 3].includes(context.kind)
  )
    return invalid();
  return ethers.getBytes(
    ethers.concat([
      CONTEXT_DOMAIN,
      new Uint8Array([
        ONCHAIN_RECOVERY_VERSION,
        context.chain === "evm" ? 1 : 2,
        context.kind,
      ]),
      recoveryWord(context.chainDomain),
      recoveryWord(context.pool, context.chain === "evm"),
      recoveryWord(context.locator),
    ]),
  );
}

function assertToken(
  tokenType: number,
  tokenAddress: string,
  chain: OnchainRecoveryContext["chain"],
): Uint8Array {
  if (
    !TOKEN_TYPES.has(tokenType) ||
    (chain === "evm" ? tokenType !== 0 : tokenType !== 3 && tokenType !== 4)
  )
    return invalid();
  const token = recoveryWord(tokenAddress, chain === "evm");
  if (chain === "evm" && token.slice(0, 12).some((byte) => byte !== 0))
    return invalid();
  return token;
}

function serializeNote(
  payload: OnchainNoteRecoveryPayload,
  context: OnchainRecoveryContext,
): Uint8Array {
  if (
    !payload ||
    ![1, 2].includes(payload.kind) ||
    payload.kind !== context.kind ||
    typeof payload.random !== "bigint" ||
    payload.random <= 0n ||
    payload.random >= BN254_SCALAR_FIELD
  )
    return invalid();
  const token = assertToken(
    payload.tokenType,
    payload.tokenAddress,
    context.chain,
  );
  if (
    payload.kind === 1 &&
    (typeof payload.value !== "bigint" ||
      payload.value <= 0n ||
      payload.value >= 1n << 120n)
  )
    return invalid();
  return ethers.getBytes(
    ethers.concat([
      ethers.toBeHex(payload.random, 32),
      token,
      new Uint8Array([payload.tokenType]),
      ...(payload.kind === 1 ? [ethers.toBeHex(payload.value, 16)] : []),
    ]),
  );
}

function parseNote(
  bytes: Uint8Array,
  context: OnchainRecoveryContext,
): OnchainNoteRecoveryPayload {
  if (
    ![1, 2].includes(context.kind) ||
    bytes.length !== (context.kind === 1 ? 81 : 65)
  )
    return invalid();
  const base = {
    random: BigInt(ethers.hexlify(bytes.slice(0, 32))),
    tokenAddress: ethers.hexlify(bytes.slice(32, 64)),
    tokenType: bytes[64] as 0 | 3 | 4,
  };
  const payload: OnchainNoteRecoveryPayload =
    context.kind === 1
      ? {
          kind: 1,
          ...base,
          value: BigInt(ethers.hexlify(bytes.slice(65, 81))),
        }
      : { kind: 2, ...base };
  serializeNote(payload, context);
  return payload;
}

async function noteKey(
  secret: Uint8Array,
  publicKey: Uint8Array,
  usage: KeyUsage,
): Promise<CryptoKey> {
  if (secret.length !== 32 || publicKey.length !== 32) return invalid();
  // Raw X25519 + HKDF, not nacl.box.before's HSalsa20 transform. Reject low
  // order public keys rather than deriving a publicly known encryption key.
  const shared = nacl.scalarMult(secret, publicKey);
  if (shared.every((byte) => byte === 0)) return invalid();
  try {
    const material = await crypto.subtle.importKey(
      "raw",
      recoveryBuffer(shared),
      "HKDF",
      false,
      ["deriveKey"],
    );
    return await crypto.subtle.deriveKey(
      {
        name: "HKDF",
        hash: "SHA-256",
        salt: recoveryBuffer(ethers.toUtf8Bytes(NOTE_KEY_DOMAIN)),
        info: recoveryBuffer(ethers.toUtf8Bytes("note-decryption-only")),
      },
      material,
      { name: "AES-GCM", length: 256 },
      false,
      [usage],
    );
  } finally {
    shared.fill(0);
  }
}

/** Only note data is encrypted to viewing keys; spending keys never use this codec. */
export async function encryptOnchainNoteRecovery(
  payload: OnchainNoteRecoveryPayload,
  inboxPublicKeyHex: string,
  context: OnchainRecoveryContext,
): Promise<Uint8Array> {
  const aad = encodeOnchainRecoveryContext(context);
  const plaintext = serializeNote(payload, context);
  const publicKey = recoveryWord(inboxPublicKeyHex);
  const ephemeral = nacl.box.keyPair();
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  try {
    const key = await noteKey(ephemeral.secretKey, publicKey, "encrypt");
    const sealed = await crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: recoveryBuffer(nonce),
        additionalData: recoveryBuffer(aad),
        tagLength: 128,
      },
      key,
      recoveryBuffer(plaintext),
    );
    return ethers.getBytes(
      ethers.concat([
        new Uint8Array([ONCHAIN_RECOVERY_VERSION, payload.kind]),
        ephemeral.publicKey,
        nonce,
        new Uint8Array(sealed),
      ]),
    );
  } catch {
    return invalid();
  } finally {
    plaintext.fill(0);
    ephemeral.secretKey.fill(0);
  }
}

/**
 * Authenticates ciphertext only. Callers MUST additionally validate the note
 * against its owner's MPK, canonical chain commitment, tree and nullifier.
 * An authenticated swap seed has no amount until a canonical swap is read.
 */
export async function decryptOnchainNoteRecovery(
  envelope: Uint8Array,
  inboxSecretKeys: readonly Uint8Array[],
  context: OnchainRecoveryContext,
): Promise<OnchainNoteRecoveryPayload> {
  const aad = encodeOnchainRecoveryContext(context);
  const expectedLength =
    NOTE_HEADER_BYTES + TAG_BYTES + (context.kind === 1 ? 81 : 65);
  if (
    !(envelope instanceof Uint8Array) ||
    ![1, 2].includes(context.kind) ||
    envelope.length !== expectedLength ||
    envelope[0] !== ONCHAIN_RECOVERY_VERSION ||
    envelope[1] !== context.kind ||
    !Array.isArray(inboxSecretKeys) ||
    inboxSecretKeys.length === 0 ||
    inboxSecretKeys.length > 8
  )
    return invalid();
  for (const candidate of inboxSecretKeys) {
    if (!(candidate instanceof Uint8Array) || candidate.length !== 32)
      return invalid();
    try {
      const key = await noteKey(candidate, envelope.slice(2, 34), "decrypt");
      const opened = new Uint8Array(
        await crypto.subtle.decrypt(
          {
            name: "AES-GCM",
            iv: recoveryBuffer(envelope.slice(34, 46)),
            additionalData: recoveryBuffer(aad),
            tagLength: 128,
          },
          key,
          recoveryBuffer(envelope.slice(NOTE_HEADER_BYTES)),
        ),
      );
      try {
        return parseNote(opened, context);
      } finally {
        opened.fill(0);
      }
    } catch {
      // Wrong-recipient records are expected during a private trial scan.
    }
  }
  return invalid();
}
